/**
 * Lo que la tarjeta del recibo muestra del recibo (DEF-054, QA de TEST
 * 05-oct-2026).
 *
 * POR QUÉ EXISTE
 * --------------
 * «Del recibo no se ve nada en la tarjeta.» QA esperaba el titular del
 * servicio, la dirección del suministro y si coincide con el inmueble de la
 * Copia Literal, que es la comprobación propia del recibo (§9.25). Los tres
 * datos existían del lado del servidor —el extractor guarda `campos` en el
 * `DOC#` y la conversación cruza la dirección con la de la Copia Literal— y
 * ninguno llegaba al panel.
 *
 * El cruce NO se calcula acá: viene resuelto en `recibo.cruce_cl` por
 * `portal_api._con_el_cruce_del_recibo`, que usa la misma
 * `teleagent.validaciones.inmueble_coincide` contra la misma referencia que la
 * conversación. Acá solo se dice en palabras.
 *
 * El titular es informativo: ninguna regla lo compara, y no hay objeción por
 * titular. Puro: lo corre `tests/unit/test_qa_test_06oct_portal.py` en Node.
 */

/** `teleagent.validaciones.CRUCE_*`, cruzados por test. */
export const CRUCE_COINCIDE = 'coincide'
export const CRUCE_NO_COINCIDE = 'no_coincide'
export const CRUCE_SIN_COPIA_LITERAL = 'sin_copia_literal'
export const CRUCE_SIN_CONFIRMAR = 'sin_confirmar'

export interface DatosDelRecibo {
  titular?: string
  direccion?: string
  cruce_cl?: string
}

export interface FilaDelRecibo {
  clave: string
  valor: string
  /** El dato no se pudo leer del documento: se dice, no se omite. */
  falta: boolean
}

/**
 * «Titular del servicio» y «Dirección del suministro», en mayúsculas como los
 * imprime el recibo (el extractor los guarda en minúsculas y sin tildes). Un
 * dato que falta se marca: sin la fila, el asesor no sabe si el recibo no lo
 * trae o si nadie lo leyó.
 */
export function filasDelRecibo(r: DatosDelRecibo | null | undefined): FilaDelRecibo[] {
  if (!r) return []
  const fila = (clave: string, valor?: string): FilaDelRecibo => {
    const limpio = (valor || '').trim()
    return limpio
      ? { clave, valor: limpio.toUpperCase(), falta: false }
      : { clave, valor: 'No se pudo leer', falta: true }
  }
  return [fila('Titular del servicio', r.titular), fila('Dirección del suministro', r.direccion)]
}

export interface CruceDelRecibo {
  texto: string
  tono: 'ok' | 'mal' | 'neutro'
}

/** El cruce con la Copia Literal en palabras, o `null` si no vino (un recibo
 *  de un expediente anterior: la referencia de hoy no es la suya). */
export function cruceDelRecibo(r: DatosDelRecibo | null | undefined): CruceDelRecibo | null {
  switch (r?.cruce_cl) {
    case CRUCE_COINCIDE:
      return { texto: 'Coincide con la Copia Literal', tono: 'ok' }
    case CRUCE_NO_COINCIDE:
      return { texto: 'No coincide con la Copia Literal', tono: 'mal' }
    case CRUCE_SIN_COPIA_LITERAL:
      return { texto: 'Sin Copia Literal para comparar', tono: 'neutro' }
    case CRUCE_SIN_CONFIRMAR:
      // `inmueble_coincide` devuelve `None`: no hay casilleros que comparar
      // (o el recibo no trae la dirección). No es «coincide» ni «no coincide».
      return { texto: 'No se pudo confirmar con la Copia Literal', tono: 'neutro' }
    default:
      return null
  }
}
